import { useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { JobDetails } from '../../Models/JobDetails';
import StepProps from '../StepProps';
import DirectReports from './Components/DirectReports';
import EmploymentType from './Components/EmploymentType';
import JobDescriptionLanguage from './Components/JobDescriptionLanguage';
import JobDetailsStepHeader from './Components/JobDetailsStepHeader';
import JobDetailsStepNavigation from './Components/JobDetailsStepNavigation';
import JobFunctionSelection from './Components/JobFunctionSelection';
import JobLocation from './Components/JobLocation/JobLocation';
import WorkArrangement from './Components/JobLocation/WorkArrangement';
import JobPostingOptions from './Components/JobPostingOptions';
import JobTitle from './Components/JobTitle';
import Salary from './Components/Salary';
import WorkExperience from './Components/WorkExperience';
import type { JobDetailsFormValues } from './JobDetailsStep.schema';
import { jobDetailsFormValues2Model, jobDetailsModel2FormValues } from './JobDetailsStepUtils';
import { useJobDetailsStepForm } from './useJobDetailsStepForm';
import { useJobFunctions } from '@/api/useJobFunctions';
import { ClientResponse } from '@/DTO/Clients/ClientResponse';
import { Currency } from '@/Models/Currency';
import { findJobCategoryByJobFunctionId } from '@/Models/JobCategory';
import ProcessingState from '@/Models/ProcessingState';

interface JobDetailsStepProps extends StepProps {
    processingState: ProcessingState;
    jobDetails: JobDetails;
    updateJobDetails: (newJobDetails: JobDetails) => Promise<void>;
    currencies: Array<Currency>;
    client: ClientResponse;
    companyName: string;
    disableJobDescriptionLanguage?: boolean;
    disableJobFunctionSelection?: boolean;
    disableHowDoYouWantToPost?: boolean;
}

const JobDetailsStep = ({
    activeStep,
    handleNext,
    handleBack,
    processingState,
    jobDetails,
    updateJobDetails,
    currencies,
    client,
    companyName,
    disableJobDescriptionLanguage = false,
    disableJobFunctionSelection = false,
    disableHowDoYouWantToPost = false,
}: JobDetailsStepProps) => {
    const { data: jobFunctionsMap } = useJobFunctions();
    const jobFunctionId = useMemo(() => {
        const jfid = jobDetails.jobFunctionId;
        if (!jfid || jfid <= 0) {
            return '';
        }

        const jobCategory = findJobCategoryByJobFunctionId(jobFunctionsMap?.jobCategories ?? [], jfid);
        return jobCategory != null ? `${jobCategory.id}` : '';
    }, [jobFunctionsMap?.jobCategories, jobDetails.jobFunctionId]);
    const form = useJobDetailsStepForm(jobDetailsModel2FormValues(jobFunctionId, jobDetails));

    const submitHandlerAsync = async (values: JobDetailsFormValues) => {
        await updateJobDetails(jobDetailsFormValues2Model(values));

        if (handleNext) {
            handleNext();
        }
    };

    return (
        <div className="mx-auto w-full max-w-4xl self-center">
            <JobDetailsStepHeader />
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(submitHandlerAsync)}>
                    <div className="border-border bg-alicia flex flex-col gap-7 rounded-lg border px-5 py-8">
                        <JobTitle />
                        <div className="flex flex-row gap-5">
                            <WorkArrangement />
                            <JobLocation />
                        </div>
                        {jobFunctionsMap && (
                            <JobFunctionSelection
                                jobFunctionsMap={jobFunctionsMap}
                                disabled={disableJobFunctionSelection}
                            />
                        )}
                        <div className="flex flex-row gap-5">
                            <WorkExperience />
                            <EmploymentType />
                        </div>
                        <DirectReports />
                        <Salary currencies={currencies} />
                        {!client.isDefault && (
                            <JobPostingOptions
                                companyName={companyName}
                                clientName={client.name}
                                disabled={disableHowDoYouWantToPost}
                            />
                        )}
                        <JobDescriptionLanguage disabled={disableJobDescriptionLanguage} />
                    </div>
                    <JobDetailsStepNavigation
                        processingState={processingState}
                        activeStep={activeStep}
                        handleBack={handleBack}
                    />
                </form>
            </FormProvider>
        </div>
    );
};

export default JobDetailsStep;
